import { supabase } from '@/lib/supabase'
import type { ObstacleRow, ObstaclePresetRow } from '@/types/obstacle'

/**
 * Fetch obstacles for a unit (soft-delete filtered)
 */
export async function fetchObstaclesByUnit(unitId: string): Promise<ObstacleRow[]> {
  const { data, error } = await supabase
    .from('obstacles')
    .select('*')
    .eq('unit_id', unitId)
    .is('deleted_at', null)
    .order('created_at')

  if (error) throw error
  return (data as unknown as ObstacleRow[]) ?? []
}

/**
 * Fetch obstacles for a location (all units)
 */
export async function fetchObstaclesByLocation(locationId: string): Promise<ObstacleRow[]> {
  const { data, error } = await supabase
    .from('obstacles')
    .select('*')
    .eq('location_id', locationId)
    .is('deleted_at', null)
    .order('unit_id')
    .order('created_at')

  if (error) throw error
  return (data as unknown as ObstacleRow[]) ?? []
}

/**
 * Fetch obstacle presets (system + org-specific)
 */
export async function fetchObstaclePresets(): Promise<ObstaclePresetRow[]> {
  const { data, error } = await supabase
    .from('obstacle_presets')
    .select('*')
    .order('sort_order')
    .order('name')

  if (error) throw error
  return (data as unknown as ObstaclePresetRow[]) ?? []
}

/**
 * Create an obstacle
 */
export async function createObstacle(
  insert: Record<string, unknown>
): Promise<ObstacleRow> {
  const { data, error } = await supabase
    .from('obstacles')
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as ObstacleRow
}

/**
 * Update an obstacle
 */
export async function updateObstacle(
  id: string,
  update: Record<string, unknown>
): Promise<ObstacleRow> {
  const { data, error } = await supabase
    .from('obstacles')
    .update(update)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as unknown as ObstacleRow
}

/**
 * Soft-delete an obstacle
 */
export async function deleteObstacle(id: string): Promise<void> {
  const { error } = await supabase
    .from('obstacles')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}
